import { forwardRef, useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Card } from "../../UI/Card.tsx";
import { BasicTable } from "../../UI/Table/BasicTable.tsx";
import { BreakableEmail } from "../../UI/Table/BreakableEmail.tsx";
import { TablePagination } from "../../UI/Table/TablePagination.tsx";
import { useClientPagination } from "../../UI/Table/useClientPagination.ts";
import { formatDate } from "../../UI/Utils/formatDate.ts";
import { SectionTitle } from "./SectionTitle.tsx";
import { StackedCell } from "./StackedCell.tsx";
import { SpeakingLevelWithGroup } from "../SpeakingLevelWithGroup.tsx";
import { ExhaustedLessonPackStudent } from "./useStudentPlanStatistics.ts";

const STUDENTS_PER_PAGE = 20;
const HIDDEN_ON_MOBILE = { className: "hidden md:table-cell" };

const columnHelper = createColumnHelper<ExhaustedLessonPackStudent>();

function daysLeftUntil(date: string, today: Dayjs): string {
  const days = Math.max(
    0,
    dayjs(date).startOf("day").diff(today.startOf("day"), "day"),
  );
  if (days === 0) return "ends today";
  return `in ${days} ${days === 1 ? "day" : "days"}`;
}

export const ExhaustedLessonPackStudentsTable = forwardRef<
  HTMLDivElement,
  {
    students: ExhaustedLessonPackStudent[];
    today: Dayjs;
    onSelectStudent: (userId: string) => void;
    onClose: () => void;
  }
>(function ExhaustedLessonPackStudentsTable(
  { students, today, onSelectStudent, onClose },
  ref,
) {
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "student",
        header: "Student",
        cell: ({ row }) => (
          <StackedCell
            primary={`${row.original.givenName} ${row.original.familyName}`}
            secondary={<BreakableEmail email={row.original.email} />}
          />
        ),
      }),
      columnHelper.accessor("speakingLevel", {
        header: "Level",
        meta: HIDDEN_ON_MOBILE,
        cell: ({ row }) => (
          <SpeakingLevelWithGroup
            speakingLevel={row.original.speakingLevel}
            userGroupName={row.original.userGroupName}
          />
        ),
      }),
      columnHelper.accessor("planEndUtc", {
        header: "Plan until",
        cell: ({ getValue }) => (
          <StackedCell
            primary={formatDate(getValue())}
            secondary={daysLeftUntil(getValue(), today)}
          />
        ),
      }),
      columnHelper.display({
        id: "lessons",
        header: "Lessons used",
        meta: HIDDEN_ON_MOBILE,
        cell: ({ row }) =>
          `${row.original.usedLessons} of ${row.original.lessonPackSize}`,
      }),
      columnHelper.accessor("nextBookedLessonStartUtc", {
        header: "Next lesson",
        meta: HIDDEN_ON_MOBILE,
        cell: ({ getValue }) => {
          const nextLesson = getValue();
          return nextLesson ? (
            formatDate(nextLesson)
          ) : (
            <span className={"text-slate-400"}>None booked</span>
          );
        },
      }),
    ],
    [today],
  );

  const { itemsOnCurrentPage, currentPage, totalPages, toPage } =
    useClientPagination(students, STUDENTS_PER_PAGE);

  const table = useReactTable({
    columns,
    data: itemsOnCurrentPage,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div ref={ref} className={"scroll-mt-4"}>
      <Card>
        <div className={"flex items-start justify-between gap-3"}>
          <div className={"flex flex-col gap-1"}>
            <SectionTitle>
              Unlimited plans with no lessons left · {students.length}
            </SectionTitle>
            <p className={"text-xs text-slate-400"}>
              Students whose active group plan has unlimited lessons per week
              from a lesson pack, who cannot book any more lesson (lessons
              already booked in the future count as used) and who have no
              upcoming group plan. Plans ending furthest in the future come
              first.
            </p>
          </div>
          <button
            type={"button"}
            onClick={onClose}
            aria-label={"Close list"}
            className={
              "shrink-0 rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
            }
          >
            <XMarkIcon className={"h-5 w-5"} />
          </button>
        </div>
        {students.length === 0 ? (
          <p className={"text-sm text-slate-400"}>
            No students in this situation.
          </p>
        ) : (
          <div className={"flex flex-col gap-4"}>
            <BasicTable
              table={table}
              compact
              scrollableMinWidthClassName={"min-w-full"}
              onRowClick={(student) => onSelectStudent(student.userId)}
            />
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onChangePage={toPage}
            />
          </div>
        )}
      </Card>
    </div>
  );
});
